import React, { useState, useEffect, useCallback } from 'react';
import { formatIngredientName } from '../utils/ingredientFormat';
import { API_URL } from '../config/api';

function StockPage({ userId, token }) {
  const [stock, setStock] = useState([]);
  const [newItem, setNewItem] = useState({ name: '', quantity: '', unit: 'kg' });
  const [editing, setEditing] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStock = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/stock/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.ok) {
        setStock(data.items || []);
      } else {
        setError(data.error || 'Error al cargar stock');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
    setLoading(false);
  }, [userId, token]);

  useEffect(() => {
    fetchStock();
  }, [fetchStock]);

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.name || !newItem.quantity) {
      setError('Completá todos los campos');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/stock/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newItem.name,
          quantity: parseFloat(newItem.quantity),
          unit: newItem.unit
        })
      });
      if (res.ok) {
        setNewItem({ name: '', quantity: '', unit: 'kg' });
        fetchStock();
        setError('');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const deleteItem = async (itemId) => {
    try {
      const res = await fetch(`${API_URL}/stock/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchStock();
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const startEdit = (item) => {
    setEditing((prev) => ({
      ...prev,
      [item._id]: { quantity: String(item.quantity), unit: item.unit || 'unidad' }
    }));
  };

  const cancelEdit = (itemId) => {
    setEditing((prev) => {
      const copy = { ...prev };
      delete copy[itemId];
      return copy;
    });
  };

  const saveEdit = async (itemId) => {
    const values = editing[itemId];
    if (!values) return;
    try {
      const res = await fetch(`${API_URL}/stock/${itemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          quantity: parseFloat(values.quantity),
          unit: values.unit
        })
      });
      const data = await res.json();
      if (!data.ok) {
        setError(data.error || 'No se pudo actualizar');
        return;
      }
      cancelEdit(itemId);
      fetchStock();
      setError('');
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  return (
    <div className="page stock-page">
      <h2>Mi Stock</h2>

      {error && <p className="error">{error}</p>}
      {loading && <p>Cargando...</p>}

      <form onSubmit={addItem} className="add-item-form">
        <input
          type="text"
          placeholder="Ingrediente"
          value={newItem.name}
          onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
          required
        />
        <input
          type="number"
          placeholder="Cantidad"
          step="0.1"
          value={newItem.quantity}
          onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
          required
        />
        <select
          value={newItem.unit}
          onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
        >
          <option value="kg">kg</option>
          <option value="g">g</option>
          <option value="ml">ml</option>
          <option value="L">L</option>
          <option value="unidad">unidad</option>
          <option value="unid">unid</option>
        </select>
        <button type="submit" className="btn-primary">Agregar</button>
      </form>

      <div className="stock-items">
        {stock && stock.length > 0 ? (
          stock.map((item) => {
            const isEditing = Boolean(editing[item._id]);
            return (
              <div key={item._id} className="stock-item">
                <div className="item-info">
                  <span className="item-name">{formatIngredientName(item.name)}</span>
                  {isEditing ? (
                    <div className="stock-edit-row">
                      <input
                        type="number"
                        step="0.1"
                        value={editing[item._id].quantity}
                        onChange={(e) => setEditing((prev) => ({
                          ...prev,
                          [item._id]: { ...prev[item._id], quantity: e.target.value }
                        }))}
                      />
                      <select
                        value={editing[item._id].unit}
                        onChange={(e) => setEditing((prev) => ({
                          ...prev,
                          [item._id]: { ...prev[item._id], unit: e.target.value }
                        }))}
                      >
                        <option value="kg">kg</option>
                        <option value="g">g</option>
                        <option value="ml">ml</option>
                        <option value="L">L</option>
                        <option value="unidad">unidad</option>
                        <option value="unid">unid</option>
                      </select>
                    </div>
                  ) : (
                    <span className="item-qty">
                      {item.quantity} {item.unit}
                    </span>
                  )}
                </div>
                <div className="stock-actions">
                  {isEditing ? (
                    <>
                      <button onClick={() => saveEdit(item._id)} className="btn-secondary">Guardar</button>
                      <button onClick={() => cancelEdit(item._id)} className="btn-secondary">Cancelar</button>
                    </>
                  ) : (
                    <button onClick={() => startEdit(item)} className="btn-secondary">Editar</button>
                  )}
                  <button
                    onClick={() => deleteItem(item._id)}
                    className="btn-delete"
                  >
                    x
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <p>Tu stock está vacío. Añadí ingredientes.</p>
        )}
      </div>
    </div>
  );
}

export default StockPage;
